import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";

export default function ViewJavakData() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 3000,
    showConfirmButton: false,
  });

  const [propertyId, setPropertyId] = useState(null);
  const [javakdatas, setJavakdatas] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [javakdate, setJavakdate] = useState("");
  const [javakpersonname, setJavakpersonname] = useState("");
  const [javaknote, setJavaknote] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ---------------- FETCH PROPERTY ---------------- */

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const snap = await getDoc(doc(db, "authroles", user.uid));
        if (snap.exists()) {
          const propId = snap.data().propertyId;
          setPropertyId(propId);
          fetchData(propId);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  /* ---------------- FETCH JAVAK DATA ---------------- */

  const fetchData = (propId) => {
    const q = query(
      collection(db, "javakregisters"),
      where("propertyId", "==", propId),
      orderBy("javakdate", "desc")
    );

    onSnapshot(q, (snapshot) => {
      setJavakdatas(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    });
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      const itemsQuery = query(
        collection(db, "javakitems"),
        where("javakid", "==", id),
        where("propertyId", "==", propertyId)
      );

      const snapshot = await getDocs(itemsQuery);

      await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));

      await deleteDoc(doc(db, "javakregisters", id));

      Toast.fire({ icon: "success", title: "Deleted Successfully" });
    } catch (error) {
      Toast.fire({ icon: "error", title: error.message });
    }
  };

  /* ---------------- EDIT ---------------- */

  const handleEdit = (item) => {
    setEditId(item.id);
    setJavakdate(item.javakdate);
    setJavakpersonname(item.javakpersonname);
    setJavaknote(item.javaknote);
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    await updateDoc(doc(db, "javakregisters", editId), {
      javakdate,
      javakpersonname,
      javaknote,
    });

    Toast.fire({ icon: "success", title: "Updated Successfully" });
    setEditOpen(false);
  };

  /* ---------------- SEARCH ---------------- */

  const filteredData = javakdatas.filter((item) =>
    item.javakpersonname
      ?.toLowerCase()
      .includes(searchText.toLowerCase())
  );

  /* ---------------- UI ---------------- */

  return (
    <Box className="content-wrapper" sx={{ p: 4, backgroundColor: "#f4f6f9", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        View Javak Data
      </Typography>

      <Card>
        <CardContent>

          <TextField
            label="Search Person"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Sr No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Person</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(item)}>
                        <EditIcon color="primary" />
                      </IconButton>

                      <IconButton onClick={() => handleDelete(item.id)}>
                        <DeleteIcon color="error" />
                      </IconButton>

                      <IconButton
                        href={`javak-details/${item.id}`}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      {moment(item.javakdate).format("DD-MM-YYYY")}
                    </TableCell>

                    <TableCell>{item.javakpersonname}</TableCell>
                    <TableCell>{item.javaknote}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />

        </CardContent>
      </Card>

      {/* ---------------- EDIT DIALOG ---------------- */}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Javak Data</DialogTitle>
        <DialogContent>

          <TextField
            fullWidth
            label="Date"
            type="date"
            value={javakdate}
            onChange={(e) => setJavakdate(e.target.value)}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Person Name"
            value={javakpersonname}
            onChange={(e) => setJavakpersonname(e.target.value)}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Note"
            value={javaknote}
            onChange={(e) => setJavaknote(e.target.value)}
            sx={{ mt: 2 }}
          />

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}